import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ZERO_COST,
  costoDeUnidadVenta,
  formatMoney,
  formatQuantity,
  newId,
  parseMoney,
  parseQuantity,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { etiquetaListaPrecio, ordenarListas, preciosParaNueva } from '../lib/catalogo.js';
import { costoConocido } from '../lib/costos.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import { nombreDePaquete, type PaqueteSugerido } from '../lib/paquetes.js';
import { usePuede } from '../lib/sesion.js';
import { normalizar } from '../lib/texto.js';
import { useT } from '../i18n/index.js';
import { etiquetaPresentacion, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Campo } from './Campo.js';
import { Ico } from './Iconos.js';
import { Margen } from './Margen.js';
import { Modal } from './Modal.js';

/** «Paquete», as a name is written in its box. */
const conMayuscula = (s: string) => s.charAt(0).toLocaleUpperCase() + s.slice(1);

/**
 * A new package of a product, from «Paquetes» (the owner, 2026-10-21): what it is called, how many
 * it holds and what it sells for on each list, every one already filled in, then «Agregar». A sheet,
 * as every form a button opens.
 *
 * What a suggestion found fills it: «Paquete», 12, and on each list 12 × the packet's price, as a
 * presentation added on the product's page starts (`preciosParaNueva`), each box changed by hand if
 * the box sells for less than its twelve packets. The name follows what it holds until somebody
 * writes one, so 100 of a staple sold by the pound becomes «Quintal» (`nombreDePaquete`); left as it
 * came, it is saved as that word, which the screens say in either language: «Pack (12)» in English.
 *
 * The prices are for whoever sets prices, as on the product's page; anybody else adds the package
 * with none, and it is not offered at the till until it has one. Whoever sees the costs sees each
 * price's margin over what the package costs, a box sold below cost in red before it is saved.
 */
export function AgregarPaquete({
  producto,
  sugerido,
  porque,
  onAgregado,
  onCerrar,
}: {
  producto: Producto;
  /** What the sales found, filling the sheet. Absent: a package added by hand, its size to be typed. */
  sugerido?: PaqueteSugerido;
  /** The sentence that says why, as its row says it. */
  porque?: string;
  /** Saved: the package as the till will name it, for the row it came from. */
  onAgregado: (etiqueta: string) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombreP = useNombreProducto();
  const puedePrecios = usePuede('editar_precios');
  const verCostos = usePuede('ver_costos');
  /**
   * The product's presentations and their prices in one reading, and nothing until it is read: the
   * prices a package starts at come from them, and «Agregar» waits for them.
   */
  const suyo = useLiveQuery(async () => {
    const suyas = (await db.unidades.where('productoId').equals(producto.id).toArray()).sort((a, b) => a.factor - b.factor);
    const precios = suyas.length ? await db.precios.where('unidadVentaId').anyOf(suyas.map((u) => u.id)).toArray() : [];
    return { suyas, precios };
  }, [producto.id]);
  const suyas = suyo?.suyas ?? [];
  const precios = suyo?.precios ?? [];
  const listas = useLiveQuery(() => db.listas.toArray(), []);
  const config = useLiveQuery(() => readSettings(), [], null);
  const existencias = useLiveQuery(
    () => (verCostos ? db.existencias.where('productoId').equals(producto.id).toArray() : []),
    [producto.id, verCostos],
    [],
  );
  const compras = useLiveQuery(() => (verCostos ? db.compras.toArray() : []), [verCostos], []);

  /** Typed, the name; absent, the one what it holds gives it. */
  const [nombreEscrito, setNombreEscrito] = useState<string | undefined>(undefined);
  const [cantidad, setCantidad] = useState(() => (sugerido ? formatQuantity(sugerido.factor) : ''));
  /** Each list's price as typed. Absent: what it holds × the smaller one's, following what it holds. */
  const [preciosEscritos, setPreciosEscritos] = useState<Record<string, string>>({});
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unidad = etiquetaUnidad(producto.unidadBase, producto.unidadBase);
  const leido = parseQuantity(cantidad);
  const factor = leido !== null && leido > 0 ? leido : null;
  const menor = suyas[0];
  const igual = factor === null ? undefined : suyas.find((u) => u.factor === factor);
  /** What is wrong with what it holds, once something is typed. */
  const malaCantidad =
    cantidad.trim() === ''
      ? null
      : factor === null
        ? t('paquetes.hoja.cantidadMal')
        : menor && factor <= menor.factor
          ? t('paquetes.hoja.masQue', { paquete: etiquetaPresentacion(menor, producto.unidadBase) })
          : igual
            ? t('paquetes.hoja.yaTiene', { paquete: etiquetaPresentacion(igual, producto.unidadBase) })
            : null;
  const valida = factor !== null && malaCantidad === null ? factor : null;

  const inicial = nombreDePaquete(producto.unidadBase, valida ?? 0);
  const palabra = conMayuscula(etiquetaUnidad(inicial, inicial));
  const nombre = nombreEscrito ?? palabra;
  /** The word it came with, in either language or capital, is saved as that word; anything else as written. */
  const guardado = [inicial, palabra].some((n) => normalizar(n) === normalizar(nombre)) ? inicial : nombre.trim();
  const etiqueta = valida !== null && guardado ? etiquetaPresentacion({ nombre: guardado, factor: valida }, producto.unidadBase) : null;

  const activas = ordenarListas(listas ?? []);
  const iniciales = puedePrecios && valida !== null ? preciosParaNueva(precios, suyas, activas, valida as Milli) : [];
  const cajas = puedePrecios
    ? activas.map((lista) => {
        const auto = iniciales.find((s) => s.listaId === lista.id)?.precio ?? null;
        const texto = preciosEscritos[lista.id] ?? (auto === null ? '' : String(auto / 100));
        const precio = texto.trim() === '' ? null : parseMoney(texto);
        return { lista, texto, precio: precio !== null && precio > 0 ? precio : null, mal: texto.trim() !== '' && (precio === null || precio <= 0) };
      })
    : [];
  /** The smaller one the prices start from: «12 × el de Unidad». */
  const desde = iniciales[0]?.desde;
  const costoBase = verCostos ? costoConocido(producto.id, 'tienda', existencias, compras) : ZERO_COST;
  const costo = valida !== null && costoBase > 0 ? costoDeUnidadVenta(costoBase, valida as Milli) : null;
  const minimoBp = config?.precioMargenMinimoBp ?? 1_000;

  const listo = suyo !== undefined && listas !== undefined && valida !== null && guardado !== '' && !cajas.some((c) => c.mal) && !ocupado;
  const alEnter = (e: React.KeyboardEvent) => e.key === 'Enter' && void agregar();

  async function agregar() {
    if (!listo || valida === null || etiqueta === null) return;
    setOcupado(true);
    setError(null);
    try {
      const unidadVentaId = newId();
      const { rechazo } = await emitirYVer('unidad_venta.creada', {
        unidadVentaId,
        productoId: producto.id,
        nombre: guardado,
        factor: valida,
      });
      if (rechazo) {
        setOcupado(false);
        setError(rechazo);
        return;
      }
      for (const c of cajas) {
        if (c.precio !== null) await emitir('precio.fijado', { unidadVentaId, listaId: c.lista.id, precio: c.precio as Centavos });
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('paquetes.hoja.noSePudo'));
      return;
    }
    onAgregado(etiqueta);
    onCerrar();
  }

  return (
    <Modal titulo={t('paquetes.nuevoDe', { producto: nombreP(producto) })} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5" data-paquete-hoja>
        {porque && (
          <p className="text-sm text-slate-400" data-paquete-hoja-porque>
            {porque}
          </p>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
          <Campo
            maxLength={40}
            etiqueta={t('comun.nombre')}
            value={nombre}
            onChange={(e) => setNombreEscrito(e.target.value)}
            onKeyDown={alEnter}
            autoComplete="off"
            data-paquete-nombre
          />
          <Campo
            etiqueta={t('detalle.enUnidad', { unidad })}
            inputMode="decimal"
            className="tabular"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            onKeyDown={alEnter}
            autoComplete="off"
            data-paquete-cantidad
          />
        </div>
        {malaCantidad && (
          <p className="-mt-3 text-sm text-rose-400" data-paquete-cantidad-mal>
            {malaCantidad}
          </p>
        )}

        {puedePrecios ? (
          <div className="flex flex-col gap-2">
            {/* A box for each list, side by side, as on «Precios/Costos». */}
            <div className={`grid gap-2 ${cajas.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {cajas.map(({ lista, texto, precio, mal }) => (
                <label key={lista.id} className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-xs text-slate-400">{etiquetaListaPrecio(lista)}</span>
                  <input
                    className={`campo tabular h-12 min-h-0 w-full min-w-0 px-3 text-right text-lg ${mal ? 'border-rose-500' : ''}`}
                    inputMode="decimal"
                    placeholder="0.00"
                    aria-label={t('paquetes.hoja.precio', { lista: etiquetaListaPrecio(lista) })}
                    aria-invalid={mal}
                    value={texto}
                    onChange={(e) => setPreciosEscritos((p) => ({ ...p, [lista.id]: e.target.value }))}
                    onKeyDown={alEnter}
                    data-paquete-precio={lista.id}
                  />
                  {verCostos && costo !== null && precio !== null && (
                    <span className="flex justify-end">
                      <Margen precio={precio as Centavos} costo={costo} minimoBp={minimoBp} compacto />
                    </span>
                  )}
                </label>
              ))}
            </div>
            {valida !== null && (desde || costo !== null) && (
              <p className="tabular text-xs text-slate-500" data-paquete-precio-desde>
                {[
                  ...(desde
                    ? [
                        t('paquetes.hoja.desde', {
                          n: formatQuantity(Math.round((valida * 1000) / desde.factor) as Milli),
                          paquete: etiquetaPresentacion(desde, producto.unidadBase),
                        }),
                      ]
                    : []),
                  ...(costo !== null ? [t('paquetes.hoja.costo', { monto: formatMoney(costo) })] : []),
                ].join(' · ')}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500" data-paquete-sin-precio>
            {t('paquetes.hoja.sinPrecio')}
          </p>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}
        <button type="button" className="btn-primario" disabled={!listo} onClick={() => void agregar()} data-paquete-agregar>
          <Ico icono="agregar" />
          {etiqueta ? t('paquetes.agregar', { paquete: etiqueta }) : t('comun.agregar')}
        </button>
      </div>
    </Modal>
  );
}
